// shared-lib/group-customization/validateLink.js
//
// Pre-write validation for a proposed link. Returns null when OK or an
// error string the UI can surface (toast / inline).
//
// Type names are compared through `canonicalType`, so a stored `rail-x` /
// `centerLine` validates exactly like `center-on` / `frame`.

const { canonicalType } = require('./linkSemantics');
const { normalizeRef, refKey } = require('./refs');
const { linkRefs, linkKey } = require('./mergeLinks');

const RAIL_RP_OVERLAP =
  'A centerline rail and a relative-position link cannot share a pair';

// Unordered object-id pairs a link holds together along X.
function pairKey(a, b) {
  return [String(a), String(b)].sort().join('|');
}

function centerOnPairs(link) {
  const ids = linkRefs(link);
  const out = new Set();
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) out.add(pairKey(ids[i], ids[j]));
  }
  return out;
}

/**
 * A `center-on` rail and an `rp` over the same pair disagree by real-world
 * noise mid-drag (the rail says "same dx", the rp bakes the current few-mm
 * offset) and the solver rejects every move — a vanity that silently
 * refuses to drag (#1501). Refuse the second of the two at creation.
 */
function railRpOverlap(link, list) {
  const t = canonicalType(link.type);
  if (t === 'center-on') {
    const pairs = centerOnPairs(link);
    return list.some(
      (l) =>
        canonicalType(l.type) === 'rp' &&
        pairs.has(pairKey(l.sourceId, l.targetId))
    );
  }
  if (t === 'rp') {
    const key = pairKey(link.sourceId, link.targetId);
    return list.some(
      (l) => canonicalType(l.type) === 'center-on' && centerOnPairs(l).has(key)
    );
  }
  return false;
}

/**
 * @param {Object} link — candidate link (already typed; usually output of
 *   createLink, but may be partial when invoked from a UI draft).
 * @param {Object[]} existingLinks — links already on the group.
 * @param {Object[]} children — current working-state children (each with
 *   `.id` and `.type`).
 */
function validateLink(link, existingLinks, children) {
  if (!link?.type) return 'Missing link type';
  const type = canonicalType(link.type);
  const list = Array.isArray(existingLinks) ? existingLinks : [];
  const childById = new Map((children || []).map((c) => [c.id, c]));

  if (type === 'rp') {
    if (!childById.has(link.sourceId)) return 'Source object not found';
    if (!childById.has(link.targetId)) return 'Target object not found';
    if (link.sourceId === link.targetId) {
      return 'Cannot link an object to itself';
    }
    if (
      list.some(
        (l) =>
          canonicalType(l.type) === 'rp' &&
          l.sourceId === link.sourceId &&
          l.targetId === link.targetId
      )
    ) {
      return 'This link already exists';
    }
    if (railRpOverlap(link, list)) return RAIL_RP_OVERLAP;
    // Cycle check: DFS from targetId following RP edges. If we reach
    // sourceId, adding source→target would close a loop.
    const adj = new Map();
    for (const l of list) {
      if (canonicalType(l.type) !== 'rp') continue;
      if (!adj.has(l.sourceId)) adj.set(l.sourceId, []);
      adj.get(l.sourceId).push(l.targetId);
    }
    const stack = [link.targetId];
    const seen = new Set();
    while (stack.length) {
      const cur = stack.pop();
      if (cur === link.sourceId) return 'This link would create a cycle';
      if (seen.has(cur)) continue;
      seen.add(cur);
      const next = adj.get(cur);
      if (next) stack.push(...next);
    }
    return null;
  }

  if (type === 'copy' || type === 'mirror') {
    const a = childById.get(link.parentAId);
    const b = childById.get(link.parentBId);
    if (!a || !b) return 'Object not found';
    if (a.id === b.id) return 'Cannot link an object to itself';
    if (a.type !== b.type) {
      return `${type} link requires same-type objects (got ${a.type} and ${b.type})`;
    }
    if (
      list.some(
        (l) =>
          canonicalType(l.type) === type &&
          ((l.parentAId === link.parentAId && l.parentBId === link.parentBId) ||
            (l.parentAId === link.parentBId && l.parentBId === link.parentAId))
      )
    ) {
      return 'This link already exists';
    }
    return null;
  }

  if (type === 'frame') {
    if (!childById.has(link.objectAId)) return 'Object A not found';
    if (!childById.has(link.objectBId)) return 'Object B not found';
    if (link.objectAId === link.objectBId) {
      return 'Cannot link an object to itself';
    }
    if (link.axis) {
      let axis;
      try {
        axis = normalizeRef(link.axis);
      } catch (e) {
        return e.message;
      }
      if (axis.kind !== 'opening' && axis.kind !== 'wall') {
        return 'A frame axis must be an opening or a wall';
      }
    }
    if (
      list.some(
        (l) =>
          canonicalType(l.type) === 'frame' &&
          ((l.objectAId === link.objectAId && l.objectBId === link.objectBId) ||
            (l.objectAId === link.objectBId && l.objectBId === link.objectAId))
      )
    ) {
      return 'This link already exists';
    }
    return null;
  }

  // Both rails validate identically; they differ only in the axis the
  // solver constrains, so a center-on and a rail-y over the same members
  // are independent constraints and must not read as duplicates of each
  // other. A center-on may also carry FIXED members (an opening, a wall),
  // which are not children and are checked only for shape.
  if (type === 'rail-y' || type === 'center-on') {
    const ids = Array.isArray(link.memberIds) ? link.memberIds : [];
    const fixed = type === 'center-on' ? link.fixedMembers || [] : [];
    if (ids.length + fixed.length < 2) return 'Rail needs at least two members';
    const seen = new Set();
    for (const id of ids) {
      if (!childById.has(id)) return 'Member object not found';
      if (seen.has(id)) return 'Duplicate member in rail';
      seen.add(id);
    }
    for (const f of fixed) {
      try {
        const k = refKey(f);
        if (seen.has(k)) return 'Duplicate member in rail';
        seen.add(k);
      } catch (e) {
        return e.message;
      }
    }
    if (railRpOverlap(link, list)) return RAIL_RP_OVERLAP;
    // Order-independent set match for "this rail already exists."
    const setKey = (l) =>
      [
        ...(l.memberIds || []).map(String),
        ...(canonicalType(l.type) === 'center-on'
          ? (l.fixedMembers || []).map(refKey)
          : []),
      ]
        .sort()
        .join('|');
    const newKey = setKey(link);
    if (
      list.some((l) => canonicalType(l.type) === type && setKey(l) === newKey)
    ) {
      return 'This rail already exists';
    }
    return null;
  }

  if (type === 'on-wall') {
    if (!childById.has(link.subjectId)) {
      return `Object ${link.subjectId} not found`;
    }
    let wall;
    try {
      wall = normalizeRef(link.wall);
    } catch (e) {
      return e.message;
    }
    if (!wall || wall.kind !== 'wall' || !wall.id) {
      return 'on-wall needs a wall ref';
    }
    if (
      list.some(
        (l) =>
          canonicalType(l.type) === 'on-wall' && l.subjectId === link.subjectId
      )
    ) {
      return 'This object is already pinned to a wall';
    }
    return null;
  }

  if (type === 'align-end') {
    let runEnd;
    let target;
    try {
      runEnd = normalizeRef(link.runEnd);
      target = normalizeRef(link.target);
    } catch (e) {
      return e.message;
    }
    if (!runEnd || runEnd.kind !== 'run-end' || !runEnd.runId) {
      return 'align-end needs a run-end ref';
    }
    if (runEnd.end !== 'start' && runEnd.end !== 'end') {
      return 'align-end run end must be start or end';
    }
    if (!target) return 'align-end needs a target ref';
    if (
      target.kind === 'opening' &&
      target.edge !== 'left' &&
      target.edge !== 'right'
    ) {
      return 'An align-end opening target needs an edge (left or right)';
    }
    if (
      target.kind === 'wall' &&
      target.at !== 'start' &&
      target.at !== 'end'
    ) {
      return 'An align-end wall target needs at: start or end';
    }
    if (
      target.kind === 'run-end' &&
      target.end !== 'start' &&
      target.end !== 'end'
    ) {
      return 'align-end run-end target must name start or end';
    }
    const key = linkKey(link);
    if (
      list.some(
        (l) => canonicalType(l.type) === 'align-end' && linkKey(l) === key
      )
    ) {
      return 'This link already exists';
    }
    return null;
  }

  return `Unknown link type: ${link.type}`;
}

module.exports = { validateLink, RAIL_RP_OVERLAP };
